import { useMemo } from "react";
import { findRawItemById } from "./utils/permissionsHelpers";

/**
 * Provides update methods for different permission structures
 */
export const usePermissionsUpdater = (activeSubTab, rawApiData = null) => {
  const updaterMethods = useMemo(() => {
    /**
     * Check if a field should be updated based on permission dependencies
     */
    const shouldUpdateFieldBasedOnDependencies = (
      field,
      permissionType,
      newValue,
      currentTab,
      contentType = null
    ) => {
      // Only apply dependency checks when setting to true
      if (!newValue) {
        return true; // Always allow unchecking
      }

      // Forms Content dependencies
      if (currentTab === "forms_content") {
        // Edit depends on View
        if (permissionType === "edit" && !field.permissions.view) {
          return false; // Can't check edit if view is unchecked
        }

        // For tabs content type, print also depends on view
        if (
          contentType === "tabs" &&
          permissionType === "print" &&
          !field.permissions.view
        ) {
          return false; // Can't check print if view is unchecked
        }
      }

      // Forms tab dependencies
      if (currentTab === "forms") {
        // Create, Edit, Delete, Print all depend on Search/View
        if (["create", "edit", "delete", "print"].includes(permissionType)) {
          if (!field.permissions.search_view) {
            return false; // Can't check dependent permissions if search_view is unchecked
          }
        }
      }

      // Tools tab dependencies
      if (currentTab === "tools") {
        // Edit depends on View/Open
        if (permissionType === "edit" && !field.permissions.view_open) {
          return false;
        }
      }

      return true; // No dependency issues, allow update
    };

    /**
     * Update flat item permissions (miscellaneous)
     */
    const updateFlatItemPermission = (
      updatedPermissions,
      itemId,
      permissionType,
      newValue
    ) => {
      const item = updatedPermissions.items.find((item) => item.id === itemId);
      if (item) {
        item.permissions[permissionType] =
          typeof newValue === "boolean"
            ? newValue
            : !item.permissions[permissionType];
      }
      return [{ id: itemId, type: "item" }];
    };

    /**
     * Update four-level permissions (Category > Parent Group > Form > Field)
     */
    const updateFourLevelPermission = (
      updatedPermissions,
      categoryId,
      parentGroupId,
      formId,
      fieldId,
      permissionType,
      newValue,
      options = {}
    ) => {
      const affectedItems = [];
      const category = updatedPermissions.categories.find(
        (cat) => cat.id === categoryId
      );

      if (!category) return affectedItems;

      // Update parent group itself
      if (parentGroupId && !formId && !fieldId) {
        const parentGroup = category.items.find(
          (item) => item.id === parentGroupId
        );

        if (!parentGroup || !parentGroup.items) return affectedItems;

        const targetValue =
          typeof newValue === "boolean"
            ? newValue
            : !parentGroup.permissions[permissionType];
        parentGroup.permissions[permissionType] = targetValue;
        affectedItems.push({ id: parentGroupId, type: "parentGroup" });

        // Update all forms and fields under this parent group
        parentGroup.items.forEach((form) => {
          // When forceFullCascade is true, always update the form
          const shouldUpdateForm =
            options.forceFullCascade ||
            shouldUpdateFieldBasedOnDependencies(
              form,
              permissionType,
              targetValue,
              activeSubTab,
              options.contentType
            );

          if (shouldUpdateForm) {
            form.permissions[permissionType] = targetValue;
            affectedItems.push({ id: form.id, type: "form" });

            // Update all fields under this form
            if (form.items) {
              form.items.forEach((field) => {
                // When forceFullCascade is true, bypass dependency checks for fields
                const shouldUpdateField =
                  options.forceFullCascade ||
                  shouldUpdateFieldBasedOnDependencies(
                    field,
                    permissionType,
                    targetValue,
                    activeSubTab,
                    options.contentType
                  );

                if (shouldUpdateField) {
                  field.permissions[permissionType] = targetValue;
                  affectedItems.push({ id: field.id, type: "field" });
                }
              });
            }
          }
        });

        // Recalculate category permissions
        recalculateCategoryPermissions(category, permissionType);

        return affectedItems;
      }

      // Update form under parent group
      const parentGroup = category.items.find(
        (item) => item.id === parentGroupId
      );
      if (!parentGroup || !parentGroup.items) return affectedItems;

      const form = parentGroup.items.find((item) => item.id === formId);
      if (!form) return affectedItems;

      if (fieldId) {
        // Update field
        const field = form.items.find((item) => item.id === fieldId);
        if (field) {
          field.permissions[permissionType] =
            typeof newValue === "boolean"
              ? newValue
              : !field.permissions[permissionType];
          affectedItems.push({ id: fieldId, type: "field" });

          // Recalculate form permissions - use every() for visual consistency
          form.permissions[permissionType] = form.items.every(
            (item) => item.permissions[permissionType]
          );

          // Recalculate parent group permissions
          parentGroup.permissions[permissionType] = parentGroup.items.every(
            (item) => item.permissions[permissionType]
          );

          // Recalculate category permissions
          recalculateCategoryPermissions(category, permissionType);
        }
      } else if (formId && !fieldId) {
        // Update form and its fields
        const targetValue =
          typeof newValue === "boolean"
            ? newValue
            : !form.permissions[permissionType];

        // Handle conditional sync
        if (options?.conditionalSync && options?.requiredPermission) {
          let anyFieldUpdated = false;

          if (form.items && form.items.length > 0) {
            form.items.forEach((field) => {
              if (
                field.permissions &&
                field.permissions[options.requiredPermission]
              ) {
                field.permissions[permissionType] = targetValue;
                affectedItems.push({ id: field.id, type: "field" });
                anyFieldUpdated = true;
              }
            });
          }

          form.permissions[permissionType] = anyFieldUpdated && targetValue;
          if (anyFieldUpdated) {
            affectedItems.push({ id: formId, type: "form" });
          }
        } else {
          // Normal update with dependency checking OR force cascade
          form.permissions[permissionType] = targetValue;
          affectedItems.push({ id: formId, type: "form" });

          if (form.items) {
            form.items.forEach((field) => {
              // When forceFullCascade is true, bypass dependency checks
              const shouldUpdateField =
                options.forceFullCascade ||
                shouldUpdateFieldBasedOnDependencies(
                  field,
                  permissionType,
                  targetValue,
                  activeSubTab,
                  options.contentType
                );

              if (shouldUpdateField) {
                field.permissions[permissionType] = targetValue;
                affectedItems.push({ id: field.id, type: "field" });
              }
            });
          }
        }

        // Recalculate parent group permissions
        parentGroup.permissions[permissionType] = parentGroup.items.every(
          (item) => item.permissions[permissionType]
        );

        // Recalculate category permissions
        recalculateCategoryPermissions(category, permissionType);
      }

      return affectedItems;
    };
    /**
     * Update three-level permissions (Category > Form > Field)
     * This is primarily used for Forms Content structure
     */
    const updateThreeLevelPermission = (
      updatedPermissions,
      categoryId,
      formId,
      fieldId,
      permissionType,
      newValue,
      options = {}
    ) => {
      const affectedItems = [];
      const category = updatedPermissions.categories.find(
        (cat) => cat.id === categoryId
      );

      if (!category) return affectedItems;

      const isPrintInTabs =
        permissionType === "print" && options.contentType === "tabs";

      // Helper functions
      const canFieldBePrinted = (fieldId) => {
        if (!isPrintInTabs) return true;
        const rawField = findRawItemById(rawApiData, fieldId);
        return rawField && rawField.canbeprinted === 1;
      };

      const getAllPrintableFields = (category) => {
        const allPrintableFields = [];
        category.items.forEach((item) => {
          // Check if this is a parent group
          if (item.isParentGroup && item.items) {
            // Iterate through forms in the parent group
            item.items.forEach((formItem) => {
              if (formItem.items) {
                formItem.items.forEach((fieldItem) => {
                  const rawField = findRawItemById(rawApiData, fieldItem.id);
                  if (rawField && rawField.canbeprinted === 1) {
                    allPrintableFields.push(fieldItem);
                  }
                });
              }
            });
          } else if (item.items) {
            // Regular form with fields
            item.items.forEach((fieldItem) => {
              const rawField = findRawItemById(rawApiData, fieldItem.id);
              if (rawField && rawField.canbeprinted === 1) {
                allPrintableFields.push(fieldItem);
              }
            });
          }
        });
        return allPrintableFields;
      };

      const recalculateFormPermission = (form) => {
        if (isPrintInTabs) {
          const printableFields = form.items.filter((field) =>
            canFieldBePrinted(field.id)
          );
          return (
            printableFields.length > 0 &&
            printableFields.every((field) => field.permissions[permissionType])
          );
        }
        return form.items.every((item) => item.permissions[permissionType]);
      };

      const recalculateCategoryPermission = () => {
        if (isPrintInTabs) {
          const allPrintableFields = getAllPrintableFields(category);
          category.permissions[permissionType] =
            allPrintableFields.length > 0 &&
            allPrintableFields.every(
              (field) => field.permissions[permissionType]
            );
        } else {
          // Check all items recursively
          const checkAllItems = (items) => {
            if (!items || items.length === 0) return true;

            return items.every((item) => {
              // If it's a parent group, check all its forms
              if (item.isParentGroup && item.items) {
                return item.items.every((form) => {
                  if (form.items) {
                    return form.items.every(
                      (field) => field.permissions[permissionType]
                    );
                  }
                  return form.permissions[permissionType];
                });
              }
              // Regular item
              if (item.items) {
                return checkAllItems(item.items);
              }
              return item.permissions[permissionType];
            });
          };

          category.permissions[permissionType] = checkAllItems(category.items);
        }
      };

      // Handle category-level update
      if (!formId && !fieldId) {
        const targetValue =
          typeof newValue === "boolean"
            ? newValue
            : !category.permissions[permissionType];
        category.permissions[permissionType] = targetValue;
        affectedItems.push({ id: categoryId, type: "category" });

        // Update all items in the category, including parent groups
        category.items.forEach((item) => {
          // Check if this is a parent group
          if (item.isParentGroup && item.items) {
            // Update the parent group itself
            const canUpdateParentGroup = shouldUpdateFieldBasedOnDependencies(
              item,
              permissionType,
              targetValue,
              activeSubTab,
              options.contentType
            );

            if (canUpdateParentGroup) {
              item.permissions[permissionType] = targetValue;
              affectedItems.push({ id: item.id, type: "parentGroup" });

              // Update all forms in the parent group
              item.items.forEach((form) => {
                const canUpdateForm = shouldUpdateFieldBasedOnDependencies(
                  form,
                  permissionType,
                  targetValue,
                  activeSubTab,
                  options.contentType
                );

                if (canUpdateForm) {
                  if (isPrintInTabs) {
                    const hasPrintableFields = form.items?.some((field) =>
                      canFieldBePrinted(field.id)
                    );
                    form.permissions[permissionType] = hasPrintableFields
                      ? targetValue
                      : false;
                    if (hasPrintableFields) {
                      affectedItems.push({ id: form.id, type: "form" });
                    }
                  } else {
                    form.permissions[permissionType] = targetValue;
                    affectedItems.push({ id: form.id, type: "form" });
                  }

                  // Update fields in the form
                  if (form.items) {
                    form.items.forEach((field) => {
                      const shouldUpdate =
                        canFieldBePrinted(field.id) &&
                        shouldUpdateFieldBasedOnDependencies(
                          field,
                          permissionType,
                          targetValue,
                          activeSubTab,
                          options.contentType
                        );

                      if (shouldUpdate) {
                        field.permissions[permissionType] = targetValue;
                        affectedItems.push({ id: field.id, type: "field" });
                      }
                    });
                  }
                }
              });
            }
          } else {
            // Regular form (not in a parent group)
            const canUpdateForm = shouldUpdateFieldBasedOnDependencies(
              item,
              permissionType,
              targetValue,
              activeSubTab,
              options.contentType
            );

            if (canUpdateForm) {
              if (isPrintInTabs) {
                const hasPrintableFields = item.items?.some((field) =>
                  canFieldBePrinted(field.id)
                );
                item.permissions[permissionType] = hasPrintableFields
                  ? targetValue
                  : false;
                if (hasPrintableFields) {
                  affectedItems.push({ id: item.id, type: "form" });
                }
              } else {
                item.permissions[permissionType] = targetValue;
                affectedItems.push({ id: item.id, type: "form" });
              }

              // Update fields
              if (item.items) {
                item.items.forEach((field) => {
                  const shouldUpdate =
                    canFieldBePrinted(field.id) &&
                    shouldUpdateFieldBasedOnDependencies(
                      field,
                      permissionType,
                      targetValue,
                      activeSubTab,
                      options.contentType
                    );

                  if (shouldUpdate) {
                    field.permissions[permissionType] = targetValue;
                    affectedItems.push({ id: field.id, type: "field" });
                  }
                });
              }
            }
          }
        });

        return affectedItems;
      }

      // Handle form and field level updates
      // First, check if the form is within a parent group
      let form = null;
      let parentGroup = null;

      // Search for the form in the category
      for (const item of category.items) {
        if (item.isParentGroup && item.items) {
          // Check within parent group
          const foundForm = item.items.find((f) => f.id === formId);
          if (foundForm) {
            form = foundForm;
            parentGroup = item;
            break;
          }
        } else if (item.id === formId) {
          // Direct form (not in parent group)
          form = item;
          break;
        }
      }

      if (!form) return affectedItems;

      if (fieldId) {
        // Field-level update
        const field = form.items?.find((item) => item.id === fieldId);
        if (!field) return affectedItems;

        field.permissions[permissionType] =
          typeof newValue === "boolean"
            ? newValue
            : !field.permissions[permissionType];
        affectedItems.push({ id: fieldId, type: "field" });

        // Recalculate form permissions
        form.permissions[permissionType] = recalculateFormPermission(form);
        affectedItems.push({
          id: formId,
          type: "form",
          expectedValue: form.permissions[permissionType],
          isRecalculated: true,
          syncValue: form.items.some(
            (item) => item.permissions[permissionType]
          ),
          visualValue: form.permissions[permissionType],
        });

        // If in parent group, recalculate parent group permissions
        if (parentGroup) {
          parentGroup.permissions[permissionType] = parentGroup.items.every(
            (f) => f.permissions[permissionType]
          );
        }

        recalculateCategoryPermission();
      } else {
        // Form-level update
        const targetValue =
          typeof newValue === "boolean"
            ? newValue
            : !form.permissions[permissionType];
        form.permissions[permissionType] = targetValue;
        affectedItems.push({ id: formId, type: "form" });

        // Handle conditional sync (for special UI cases, not cross-tab sync)
        if (options?.conditionalSync && options?.requiredPermission) {
          let anyFieldUpdated = false;
          form.items?.forEach((field) => {
            if (field.permissions[options.requiredPermission]) {
              if (
                isPrintInTabs &&
                targetValue &&
                !canFieldBePrinted(field.id)
              ) {
                return;
              }
              field.permissions[permissionType] = targetValue;
              affectedItems.push({ id: field.id, type: "field" });
              anyFieldUpdated = true;
            }
          });
          if (!anyFieldUpdated) {
            form.permissions[permissionType] = false;
          }
        } else {
          // Normal update - update all fields with dependency checking
          form.items?.forEach((field) => {
            const shouldUpdate =
              canFieldBePrinted(field.id) &&
              shouldUpdateFieldBasedOnDependencies(
                field,
                permissionType,
                targetValue,
                "forms_content",
                options.contentType || "general"
              );

            if (shouldUpdate) {
              field.permissions[permissionType] = targetValue;
              affectedItems.push({ id: field.id, type: "field" });
            }
          });
        }

        // Recalculate form permissions after field updates
        form.permissions[permissionType] = recalculateFormPermission(form);

        // If in parent group, recalculate parent group permissions
        if (parentGroup) {
          parentGroup.permissions[permissionType] = parentGroup.items.every(
            (f) => f.permissions[permissionType]
          );
        }

        recalculateCategoryPermission();
      }

      return affectedItems;
    };

    /**
     * Update Forms three-level permissions (Category > Parent > Child)
     */
    const updateFormsThreeLevelPermission = (
      updatedPermissions,
      categoryId,
      parentId,
      childId,
      permissionType,
      newValue,
      options = {}
    ) => {
      const affectedItems = [];
      const category = updatedPermissions.categories.find(
        (cat) => cat.id === categoryId
      );

      if (!category) return affectedItems;

      // Find parent item
      const parentItem = category.items.find((item) => item.id === parentId);
      if (!parentItem) return affectedItems;

      if (childId) {
        // Update child
        const child = parentItem.items.find((item) => item.id === childId);
        if (child) {
          child.permissions[permissionType] =
            typeof newValue === "boolean"
              ? newValue
              : !child.permissions[permissionType];
          affectedItems.push({ id: childId, type: "child" });

          // Recalculate parent permissions
          parentItem.permissions[permissionType] = parentItem.items.every(
            (item) => item.permissions[permissionType]
          );

          // Recalculate category permissions
          recalculateCategoryPermissionsForForms(category, permissionType);
        }
      } else if (parentId && !childId) {
        // Update parent and all children
        const targetValue =
          typeof newValue === "boolean"
            ? newValue
            : !parentItem.permissions[permissionType];
        parentItem.permissions[permissionType] = targetValue;
        affectedItems.push({ id: parentId, type: "parent" });

        // Recalculate parent permissions
        if (parentItem.items && parentItem.items.length > 0) {
          // For print permission, only consider printable children
          if (permissionType === "print" && rawApiData) {
            const printableChildren = parentItem.items.filter((child) => {
              const rawChild = rawApiData.formS_TABLE?.find(
                (raw) => raw.itemid === child.id
              );
              return !rawChild || rawChild.canbeprinted === 1;
            });

            if (printableChildren.length > 0) {
              parentItem.permissions[permissionType] = printableChildren.every(
                (item) => item.permissions[permissionType]
              );
            } else {
              parentItem.permissions[permissionType] = false;
            }
          } else {
            // Normal logic for other permissions
            parentItem.permissions[permissionType] = parentItem.items.every(
              (item) => item.permissions[permissionType]
            );
          }
        }

        // Recalculate category permissions
        recalculateCategoryPermissionsForForms(category, permissionType);
      }

      return affectedItems;
    };

    /**
     * Update standard two-level hierarchical permissions
     */
    const updateHierarchicalPermission = (
      updatedPermissions,
      categoryId,
      itemId,
      permissionType,
      newValue,
      options = {}
    ) => {
      const affectedItems = [];
      const category = updatedPermissions.categories.find(
        (cat) => cat.id === categoryId
      );

      if (!category) return affectedItems;

      if (itemId) {
        // Update item
        const item = category.items.find((item) => item.id === itemId);
        if (item) {
          const targetValue =
            typeof newValue === "boolean"
              ? newValue
              : !item.permissions[permissionType];

          item.permissions[permissionType] = targetValue;
          affectedItems.push({ id: itemId, type: "item" });

          // Update children if any
          if (item.items && item.items.length > 0) {
            item.items.forEach((field) => {
              if (
                shouldUpdateChild(field, permissionType) &&
                shouldUpdateFieldBasedOnDependencies(
                  field,
                  permissionType,
                  targetValue,
                  activeSubTab
                )
              ) {
                field.permissions[permissionType] = targetValue;
                affectedItems.push({ id: field.id, type: "field" });
              }
            });
          }

          // Recalculate category permissions
          recalculateCategoryPermissionsForForms(category, permissionType);
        }
      } else {
        // Category-level change
        const targetValue =
          typeof newValue === "boolean"
            ? newValue
            : !category.permissions[permissionType];
        category.permissions[permissionType] = targetValue;
        affectedItems.push({ id: categoryId, type: "category" });

        // Update all items
        category.items.forEach((item) => {
          let shouldUpdateItem = true;

          if (permissionType === "print") {
            const rawItem = rawApiData?.formS_TABLE?.find(
              (raw) => raw.itemid === item.id
            );
            shouldUpdateItem = rawItem && rawItem.canbeprinted === 1;
          }

          const shouldCheckDependencies = !(
            permissionType === "print" && shouldUpdateItem
          );

          if (
            shouldUpdateItem &&
            (shouldCheckDependencies
              ? shouldUpdateFieldBasedOnDependencies(
                  item,
                  permissionType,
                  targetValue,
                  activeSubTab
                )
              : true)
          ) {
            item.permissions[permissionType] = targetValue;
            affectedItems.push({ id: item.id, type: "item" });
          }

          // Update nested items if they exist
          if (item.items && item.items.length > 0) {
            item.items.forEach((field) => {
              let shouldUpdateNestedItem = true;

              if (permissionType === "print") {
                const rawField = rawApiData?.formS_TABLE?.find(
                  (raw) => raw.itemid === field.id
                );
                shouldUpdateNestedItem =
                  rawField && rawField.canbeprinted === 1;
              }

              const shouldCheckNestedDependencies = !(
                permissionType === "print" && shouldUpdateNestedItem
              );

              if (
                shouldUpdateNestedItem &&
                (shouldCheckNestedDependencies
                  ? shouldUpdateFieldBasedOnDependencies(
                      field,
                      permissionType,
                      targetValue,
                      activeSubTab
                    )
                  : true)
              ) {
                field.permissions[permissionType] = targetValue;
                affectedItems.push({ id: field.id, type: "field" });
              }
            });
          }
        });
      }

      return affectedItems;
    };

    /**
     * Check if a child should be updated (e.g., print permission checks)
     */
    const shouldUpdateChild = (child, permissionType) => {
      // For print permission in forms, check canbeprinted
      if (
        activeSubTab === "forms" &&
        permissionType === "print" &&
        rawApiData
      ) {
        const rawItem = findRawItemById(rawApiData, child.id);
        return rawItem && rawItem.canbeprinted === 1;
      }
      return true;
    };

    /**
     * Recalculate category permissions for standard structure
     */
    const recalculateCategoryPermissions = (category, permissionType) => {
      const allLeafPermissions = [];

      category.items.forEach((item) => {
        if (item.isParentGroup && item.items) {
          item.items.forEach((form) => {
            if (form.items) {
              form.items.forEach((field) => {
                // For print permission in Forms Content tabs, check canbeprinted
                if (
                  permissionType === "print" &&
                  activeSubTab === "forms_content" &&
                  rawApiData
                ) {
                  const rawField = rawApiData.formS_TABS_TABLE?.find(
                    (raw) => raw.itemid === field.id
                  );

                  // Skip fields that cannot be printed
                  if (rawField && rawField.canbeprinted === 0) {
                    return;
                  }
                }

                allLeafPermissions.push(field.permissions[permissionType]);
              });
            }
          });
        } else if (item.items) {
          item.items.forEach((field) => {
            // For print permission, check canbeprinted
            if (permissionType === "print" && rawApiData) {
              const tableToCheck =
                activeSubTab === "forms_content"
                  ? rawApiData.formS_TABS_TABLE
                  : rawApiData.formS_TABLE;

              const rawField = tableToCheck?.find(
                (raw) => raw.itemid === field.id
              );

              // Skip fields that cannot be printed
              if (rawField && rawField.canbeprinted === 0) {
                return;
              }
            }

            allLeafPermissions.push(field.permissions[permissionType]);
          });
        }
      });

      // Only update if we have printable items
      if (allLeafPermissions.length > 0) {
        category.permissions[permissionType] = allLeafPermissions.every(
          (perm) => perm
        );
      } else if (permissionType === "print") {
        // No printable items means print should be disabled
        category.permissions[permissionType] = false;
      } else {
        // For non-print permissions, use the normal logic
        category.permissions[permissionType] =
          allLeafPermissions.length > 0
            ? allLeafPermissions.every((perm) => perm)
            : false;
      }
    };

    /**
     * Recalculate category permissions for Forms structure
     */
    const recalculateCategoryPermissionsForForms = (
      category,
      permissionType
    ) => {
      const allLeafPermissions = [];

      category.items.forEach((item) => {
        // For print permission, check if item can be printed
        if (permissionType === "print" && rawApiData) {
          const rawItem = rawApiData.formS_TABLE?.find(
            (raw) => raw.itemid === item.id
          );

          // Skip items that cannot be printed
          if (rawItem && rawItem.canbeprinted === 0) {
            return; // Skip this item in the calculation
          }
        }

        if (item.items && item.items.length > 0) {
          item.items.forEach((child) => {
            // For print permission, check if child can be printed
            if (permissionType === "print" && rawApiData) {
              const rawChild = rawApiData.formS_TABLE?.find(
                (raw) => raw.itemid === child.id
              );

              // Skip children that cannot be printed
              if (rawChild && rawChild.canbeprinted === 0) {
                return; // Skip this child
              }
            }

            allLeafPermissions.push(child.permissions[permissionType]);
          });
        } else {
          allLeafPermissions.push(item.permissions[permissionType]);
        }
      });

      // Only update if we have printable items
      if (allLeafPermissions.length > 0) {
        category.permissions[permissionType] = allLeafPermissions.every(
          (perm) => perm
        );
      } else if (permissionType === "print") {
        // No printable items means print should be disabled
        category.permissions[permissionType] = false;
      }
    };

    /**
     * Main update function
     */
    const updatePermissions = (
      updatedPermissions,
      categoryId,
      itemId,
      permissionType,
      newValue,
      options
    ) => {
      if (updatedPermissions.isFlat) {
        return updateFlatItemPermission(
          updatedPermissions,
          itemId,
          permissionType,
          newValue
        );
      }

      if (
        activeSubTab === "forms_content" &&
        (!itemId || itemId === categoryId)
      ) {
        return updateThreeLevelPermission(
          updatedPermissions,
          categoryId,
          null,
          null,
          permissionType,
          newValue,
          options
        );
      }

      // Handle parent group updates
      if (options?.isParentGroup) {
        return updateFourLevelPermission(
          updatedPermissions,
          categoryId,
          itemId,
          null,
          null,
          permissionType,
          newValue,
          options
        );
      }

      // Handle form-level clicks in 4-level structure (form under parent group)
      if (options?.parentGroupId && options?.isFormLevelClick) {
        return updateFourLevelPermission(
          updatedPermissions,
          categoryId,
          options.parentGroupId,
          itemId, // This is form.id
          null, // No fieldId - we're updating the form itself
          permissionType,
          newValue,
          options
        );
      }

      // Handle 4-level structure (field clicks)
      if (options?.parentGroupId && options?.formId) {
        return updateFourLevelPermission(
          updatedPermissions,
          categoryId,
          options.parentGroupId,
          options.formId,
          itemId,
          permissionType,
          newValue,
          options
        );
      }

      // Handle form-level clicks in 3-level structure (form directly under category)
      if (
        options?.isFormLevelClick &&
        options?.formId &&
        !options?.parentGroupId
      ) {
        return updateThreeLevelPermission(
          updatedPermissions,
          categoryId,
          itemId, // This is the full form.id
          null, // fieldId is null - we're updating the form itself
          permissionType,
          newValue,
          options
        );
      }

      // Handle three-level Forms Content structure (field clicks)
      if (options?.formId) {
        return updateThreeLevelPermission(
          updatedPermissions,
          categoryId,
          options.formId,
          itemId === options.formId ? null : itemId,
          permissionType,
          newValue,
          options
        );
      }

      // Handle Forms three-level structure
      if (options?.parentId) {
        return updateFormsThreeLevelPermission(
          updatedPermissions,
          categoryId,
          options.parentId,
          itemId,
          permissionType,
          newValue,
          options
        );
      }

      // Standard hierarchical update
      return updateHierarchicalPermission(
        updatedPermissions,
        categoryId,
        itemId,
        permissionType,
        newValue,
        options
      );
    };

    return {
      updatePermissions,
    };
  }, [activeSubTab, rawApiData]);

  return updaterMethods;
};
